import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { UserManagementState } from '../../user-management/state/user-management.state';
import { AuthService } from '../services/auth.service';
import { filter, map, Observable, take } from 'rxjs';
import { AccountingRoles, EntraRoles, GlobalRoles } from '@monorepo/interfaces';
import { hasRole } from '../../shared/utils/has-role.util';

@Injectable({
    providedIn: 'root'
})
export class UserManagementGuard implements CanActivate {
    private store = inject(Store);
    private router = inject(Router);
    private authService = inject(AuthService);

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        if (!this.authService.isAuthenticated()) {
            if (route.routeConfig?.path === 'home') {
                return new Observable(observer => observer.next(true));
            }
            return new Observable(observer => observer.next(false));
        }

        const requiredRoles: GlobalRoles[] | AccountingRoles[] = route.data['roles'] || [];
        const requiredEntraRoles: EntraRoles[] = route.data['entraRoles'] || [];

        return this.store.select(UserManagementState.isMeLoading).pipe(
            filter(isLoading => !isLoading), // Wait until loading is complete
            take(1),
            map(() => {
                const user = this.store.selectSignal(UserManagementState.user)();
                if (user) {
                    const userEntraRoles = this.authService.userEntraRoles();
                    const hasRequiredEntraRole = requiredEntraRoles.some(role => userEntraRoles.includes(role));
                    const hasRequiredRole = requiredRoles.length > 0 ? hasRole(user, requiredRoles) : true;
                    if (!hasRequiredRole && !hasRequiredEntraRole) {
                        this.router.navigate(['/unauthorized']);
                        return false;
                    }
                    return true;
                }
                this.router.navigate(['/unauthorized']);
                return false;
            })
        );
    }
}
